import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  getConnections,
  getAnalyticAccounts,
  getAnalyticSummary,
  getAnalyticAccountDetail,
  getAnalyticLines,
} from '../../api/api';

function AnalyticAccounts() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountLines, setAccountLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [debugUrl, setDebugUrl] = useState(null);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await getConnections();
      setConnections(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedConnection(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  useEffect(() => {
    if (selectedConnection) {
      loadAccounts();
      loadSummary();
    }
  }, [selectedConnection, searchTerm, activeOnly]);

  const loadAccounts = async () => {
    setLoading(true);
    setError(null);
    setDebugUrl(null);
    try {
      const response = await getAnalyticAccounts(selectedConnection, {
        search: searchTerm,
        active_only: activeOnly,
      });
      setAccounts(response.data.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      const errorData = error.response?.data;
      if (errorData) {
        setError(errorData.error || 'Error al cargar cuentas analíticas');
        if (errorData.debug_url) {
          setDebugUrl(errorData.debug_url);
        }
      } else {
        setError('Error al cargar cuentas analíticas');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await getAnalyticSummary(selectedConnection);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleViewDetails = async (account) => {
    setSelectedAccount(account);
    setDetailDialogOpen(true);
    
    // Load lines for this account
    setLinesLoading(true);
    try {
      const response = await getAnalyticLines(selectedConnection, account.id, {
        limit: 20,
      });
      setAccountLines(response.data.data || []);
    } catch (error) {
      console.error('Error loading account lines:', error);
    } finally {
      setLinesLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedAccount(null);
    setAccountLines([]);
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') {
      value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
        📊 Cuentas Analíticas
      </Typography>

      {/* Connection Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selecciona una conexión
          </Typography>
          <TextField
            fullWidth
            select
            value={selectedConnection || ''}
            onChange={(e) => setSelectedConnection(parseInt(e.target.value))}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">-- Selecciona una conexión --</option>
            {connections.map((conn) => (
              <option key={conn.id} value={conn.id}>
                {conn.name}
              </option>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {selectedConnection && (
        <>
          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>
                      Total de Cuentas
                    </Typography>
                    <Typography variant="h4">{summary.total_accounts}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.active_accounts} activas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>
                      Balance Total
                    </Typography>
                    <Typography variant="h5">{formatCurrency(summary.total_balance)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Promedio: {formatCurrency(summary.average_balance)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>
                      Cuentas Activas
                    </Typography>
                    <Typography variant="h4">{summary.active_accounts}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {summary.inactive_accounts} inactivas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                      />
                    }
                    label="Solo cuentas activas"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Accounts Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Lista de Cuentas Analíticas ({accounts.length})
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {error}
                    </Typography>
                    {debugUrl && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Para más información, usa el endpoint: <code>{debugUrl}</code>
                      </Typography>
                    )}
                  </Box>
                </Alert>
              )}
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : accounts.length === 0 && !error ? (
                <Alert severity="info">No hay cuentas analíticas disponibles</Alert>
              ) : accounts.length > 0 && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Balance
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                          Estado
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>{account.name}</TableCell>
                          <TableCell>{account.code || '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: '600' }}>
                            {formatCurrency(account.balance || 0)}
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: account.active ? '#c8e6c9' : '#ffccbc',
                                color: account.active ? '#1b5e20' : '#bf360c',
                              }}
                            >
                              {account.active ? 'Activa' : 'Inactiva'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              startIcon={<InfoIcon />}
                              onClick={() => handleViewDetails(account)}
                            >
                              Detalles
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Detail Dialog */}
          <Dialog open={detailDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              Detalles de la Cuenta: {selectedAccount?.name}
            </DialogTitle>
            <DialogContent>
              {selectedAccount && (
                <>
                  <Box sx={{ mb: 3, mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Código
                        </Typography>
                        <Typography variant="h6">{selectedAccount.code || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Balance
                        </Typography>
                        <Typography variant="h6">{formatCurrency(selectedAccount.balance || 0)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Estado
                        </Typography>
                        <Typography variant="body2">
                          {selectedAccount.active ? 'Activa' : 'Inactiva'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Fecha de Creación
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedAccount.create_date).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Lines Table */}
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Últimos Movimientos
                  </Typography>
                  {linesLoading ? (
                    <CircularProgress />
                  ) : accountLines.length === 0 ? (
                    <Alert severity="info">No hay movimientos para esta cuenta</Alert>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                              Monto
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accountLines.map((line) => (
                            <TableRow key={line.id} hover>
                              <TableCell>{new Date(line.date).toLocaleDateString()}</TableCell>
                              <TableCell>{line.name}</TableCell>
                              <TableCell align="right">{formatCurrency(line.amount || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default AnalyticAccounts;
